import dayjs from 'dayjs'
import { Dialog } from 'quasar'
import { getI18n } from '@/plugins/i18n'

export const isNotEmpty = (value) => value !== undefined && value !== null && value !== ''

const rootFontSize = () => parseFloat(getComputedStyle(document.documentElement).fontSize) || 16

export const remToPx = (rem) => rem * rootFontSize()

export const pxToRem = (px) => px / rootFontSize()

export const fixedDecimal = (value, decimals = 2) => {
  const number = Number(value) || 0
  const factor = 10 ** decimals
  return Math.sign(number) * Math.round(Math.abs(number) * factor * (1 + Number.EPSILON)) / factor
}

export const truncateStart = (value, max = 32) => {
  const text = String(value ?? '')
  return text.length > max ? `…${text.slice(text.length - max)}` : text
}

export const getPropertyName = (obj, expression) => {
  var res = {}
  Object.keys(obj).forEach(key => {
    res[key] = key
  })
  return expression(res)
}

export const stringToHsl = (str) => {
  if (!str) return [0, 0, 0]

  // FNV-1a hash
  let hash = 2166136261
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i)
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24)
  }

  hash >>>= 0

  const hue = (hash & 0xFF) + ((hash >> 8) & 0xFF) % 360 // 0–359
  const saturation = 60 + ((hash >> 16) & 0x1F) // 60–91%
  const lightness = 55 + ((hash >> 21) & 0x1F) // 55–76%

  return [hue % 360, saturation, lightness]
}

export const stringToColor = (str) => {
  return `rgb(${stringToRgb(str).join(',')})`
}

export const stringToRgb = (str) => {
  const stringToHslValue = stringToHsl(str)
  return hslToRgb(stringToHslValue[0], stringToHslValue[1], stringToHslValue[2])
}

export const textColor = (backgroundColor) => {
  const el = document.createElement('div')
  el.style.color = backgroundColor
  document.body.appendChild(el)

  const rgb = getComputedStyle(el).color.match(/\d+/g)
  document.body.removeChild(el)

  if (!rgb || rgb.length < 3) return 'black'

  const [r, g, b] = rgb.map(Number)
  const brightness = (r * 299 + g * 587 + b * 114) / 1000

  return brightness < 130 ? 'white' : 'black'
}

const hslToRgb = (h, s, l) => {
  h = h / 360
  s = s / 100
  l = l / 100

  let r, g, b

  if (s === 0) {
    r = g = b = l
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1
      if (t > 1) t -= 1
      if (t < 1 / 6) return p + (q - p) * 6 * t
      if (t < 1 / 2) return q
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
      return p
    }

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q

    r = hue2rgb(p, q, h + 1 / 3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1 / 3)
  }

  return [
    Math.round(r * 255),
    Math.round(g * 255),
    Math.round(b * 255)
  ]
}

export const getInitials = (name) => (name ?? '')
  .split(/\s+/)
  .filter(word => /[\p{L}\p{N}]/u.test(word))
  .slice(0, 2)
  .map(word => word[0])
  .join('')
  .toUpperCase()

export const formatDate = (date = new Date()) => {
  if (date) {
    return dayjs(date).format('YYYY/MM/DD')
  }
  return ''
}

export const formatShortDate = (date = new Date(), locale = null) => {
  if (date) {
    locale = locale || getI18n().locale.value
    return dayjs(date).locale(locale).format('L')
  }
  return ''
}

export const formatDayMonthDate = (date = new Date(), locale = null) => {
  if (date) {
    locale = locale || getI18n().locale.value
    return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit' }).format(dayjs(date).toDate())
  }
  return ''
}

export const formatShortDateTime = (date = new Date(), locale = null) => {
  if (date) {
    locale = locale || getI18n().locale.value
    return new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' }).format(dayjs(date).toDate())
  }
  return ''
}

export const formatTime = (date = new Date()) => {
  if (date) {
    return dayjs(date).format('HH:mm')
  }
  return ''
}

export const formatString = (value, format) => {
  if (!value) return ''
  for (const char of value) {
    format = format.replace('#', char)
  }
  return format
}

export const stringFormats = {
  PHONE: '+## (###) ###-####',
}

// eslint-disable-next-line no-extend-native
Array.prototype.sortBy = function(fn) {
  return this.sort((a, b) => {
    return fn(a)?.localeCompare(fn(b))
  })
}

export const downloadBlobUrl = (blobUrl, fileName) => {
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

const shareFileAsync = async(file) => {
  try {
    await navigator.share({ files: [file] })
  } catch (err) {
    if (err.name === 'AbortError') return
    // the fetch can outlive the tap's user activation on iOS
    if (err.name === 'NotAllowedError') {
      Dialog.create({
        message: file.name,
        ok: getI18n().t('common.open'),
        cancel: true,
      }).onOk(() => shareFileAsync(file))
      return
    }
    console.error('Failed to share file', err)
  }
}

export const downloadFileAsync = async(fileName, fileUrl) => {
  try {
    const response = await fetch(fileUrl)
    const blob = await response.blob()
    // iOS standalone PWA opens <a download> blobs in a preview with no back button
    const file = new File([blob], fileName, { type: blob.type })
    if (isIOS() && navigator.canShare?.({ files: [file] })) {
      await shareFileAsync(file)
      return
    }
    const blobUrl = window.URL.createObjectURL(blob)
    downloadBlobUrl(blobUrl, fileName)
    window.URL.revokeObjectURL(blobUrl)
  } catch (error) {
    console.error('Error downloading file:', error)
    window.open(fileUrl, '_blank')
  }
}
